import React, { ChangeEvent, FormEvent, useState, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  SwipeableDrawer,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Badge,
  IconButton,
} from '@mui/material';
import { SecondaryButton } from '../Button';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import PriceField from '../PriceField';
import { ALL_CATEGORIES } from '../../constants';
import { Filters, Sort, SORT_OPTIONS } from '../../models/product';

interface Props {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  sort: Sort;
  setSort: React.Dispatch<React.SetStateAction<Sort>>;
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
}

const SearchBar = ({ filters, setFilters, sort, setSort, searchText, setSearchText }: Props) => {
  const [searchFocused, setSearchFocused] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<Filters>(filters);
  const [draftSort, setDraftSort] = useState<Sort>(sort);

  useEffect(() => {
    setDraftFilters(filters);
  }, [filters]);

  useEffect(() => {
    setDraftSort(sort);
  }, [sort]);

  const toggleFilterDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event?.type === 'keydown' && ['Tab', 'Shift'].includes((event as React.KeyboardEvent).key)) {
      return;
    }
    setFilterOpen(open);
  };

  const toggleSortDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event?.type === 'keydown' && ['Tab', 'Shift'].includes((event as React.KeyboardEvent).key)) {
      return;
    }
    setSortOpen(open);
  };

  const applyFilters = (e: FormEvent) => {
    e.preventDefault();
    setFilters({
      maximumPrice: draftFilters.maximumPrice,
      minimumPrice: draftFilters.minimumPrice,
      categories: { ...draftFilters.categories },
    });
    setFilterOpen(false);
  };

  const applySort = (e: FormEvent) => {
    e.preventDefault();
    setSort(draftSort);
    setSortOpen(false);
  };

  const updateDraftFilters = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked, id } = e.target;
    if (type === 'checkbox') {
      setDraftFilters((prev) => ({
        ...prev,
        categories: { ...prev.categories, [name]: checked },
      }));
    } else if (id === 'minimumPrice' || id === 'maximumPrice') {
      setDraftFilters((prev) => ({
        ...prev,
        [id]: value.replace(/^0+(?=\d)/, ''),
      }));
    }
  };

  const updateDraftSort = (e: ChangeEvent<HTMLInputElement>) => {
    setDraftSort(e.target.value as Sort);
  };

  const clearFilters = () => {
    const emptyFilters: Filters = {
      maximumPrice: '',
      minimumPrice: '',
      categories: ALL_CATEGORIES.reduce<Record<string, boolean>>((acc, key) => {
        acc[key] = false;
        return acc;
      }, {}),
    };
    setFilters(emptyFilters);
    setDraftFilters(emptyFilters);
    setFilterOpen(false);
  };

  const countFilters = () =>
    ALL_CATEGORIES.filter((cat) => filters.categories[cat]).length +
    (filters.minimumPrice ? 1 : 0) +
    (filters.maximumPrice ? 1 : 0);

  const sortButton = (
    <IconButton
      color="inherit"
      aria-label="sort"
      sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 2, flexShrink: 0, minWidth: 40, width: 40, height: 40 }}
      onClick={toggleSortDrawer(true)}
    >
      <Badge badgeContent={sort !== 'Default' ? 1 : 0} color="primary" invisible={sort === 'Default'} variant="dot">
        <SwapVertIcon />
      </Badge>
    </IconButton>
  );

  const filterButton = (
    <IconButton
      color="inherit"
      aria-label="filter"
      sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 2, flexShrink: 0, minWidth: 40, width: 40, height: 40 }}
      onClick={toggleFilterDrawer(true)}
    >
      <Badge badgeContent={countFilters()} color="primary" invisible={countFilters() === 0}>
        <FilterAltIcon />
      </Badge>
    </IconButton>
  );

  const searchField = (
    <TextField
      variant="outlined"
      placeholder="Search products..."
      size="small"
      sx={{ width: '100%', minWidth: 0 }}
      value={searchText}
      onChange={(e) => setSearchText(e.target.value)}
      onFocus={() => setSearchFocused(true)}
      onBlur={() => setSearchFocused(false)}
      slotProps={{
        input: {
          startAdornment:
            !searchFocused && !searchText ? (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ) : null,
        },
      }}
    />
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          mb: 2,
          px: 2,
          boxSizing: 'border-box',
        }}
      >
        {sortButton}
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>{searchField}</Box>
        {filterButton}
      </Box>

      <SwipeableDrawer
        anchor="bottom"
        open={filterOpen}
        onClose={toggleFilterDrawer(false)}
        onOpen={toggleFilterDrawer(true)}
        slotProps={{
          paper: { sx: { borderTopLeftRadius: 24, borderTopRightRadius: 24 } },
        }}
      >
        <Box component="form" onSubmit={applyFilters} p={2}>
          <Typography variant="h5" component="h2" textAlign="center" gutterBottom>
            Filters
          </Typography>
          <Typography variant="h6" component="h3" gutterBottom>
            Price range
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-around', gap: 2, flexWrap: 'wrap' }}>
            <PriceField
              id="minimumPrice"
              label="Minimum"
              value={draftFilters.minimumPrice}
              onChange={updateDraftFilters}
            />
            <PriceField
              id="maximumPrice"
              label="Maximum"
              value={draftFilters.maximumPrice}
              onChange={updateDraftFilters}
            />
          </Box>
          <Typography variant="h6" component="h3" gutterBottom pt={2}>
            Category
          </Typography>
          <FormGroup id="category-filter">
            {ALL_CATEGORIES.map((category) => (
              <FormControlLabel
                key={category}
                control={
                  <Checkbox
                    name={category}
                    checked={!!draftFilters.categories[category]}
                    onChange={updateDraftFilters}
                  />
                }
                label={category}
              />
            ))}
          </FormGroup>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
            <SecondaryButton onClick={clearFilters}>
              Clear Filters
            </SecondaryButton>
            <SecondaryButton type="submit">
              Apply Filters
            </SecondaryButton>
          </Box>
        </Box>
      </SwipeableDrawer>

      <SwipeableDrawer
        anchor="bottom"
        open={sortOpen}
        onClose={toggleSortDrawer(false)}
        onOpen={toggleSortDrawer(true)}
        slotProps={{
          paper: { sx: { borderTopLeftRadius: 24, borderTopRightRadius: 24 } },
        }}
      >
        <Box component="form" onSubmit={applySort} p={2}>
          <Typography variant="h5" component="h2" textAlign="center" gutterBottom>
            Sort
          </Typography>
          <FormControl>
            <FormLabel>Order</FormLabel>
            <RadioGroup name="sort-options" value={draftSort} onChange={updateDraftSort}>
              {SORT_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio />}
                  label={option}
                />
              ))}
            </RadioGroup>
          </FormControl>
          <Box sx={{ mt: 2 }}>
            <SecondaryButton type="submit" width="100%">
              Apply
            </SecondaryButton>
          </Box>
        </Box>
      </SwipeableDrawer>
    </>
  );
};

export default SearchBar;
